import React from "react";

const RELOAD_GUARD_KEY = "inara:chunk-reload-attempted";

/** True for the "stale build" class of error: the browser tried to fetch a
 * lazily-loaded JS chunk (via `React.lazy`/`next/dynamic`, see
 * `src/lib/next-compat.tsx`) whose hashed filename no longer exists on the
 * server because a new build was deployed while this tab sat open/backgrounded.
 * Different browsers phrase this differently, so match broadly. */
function isStaleChunkError(error: unknown): boolean {
    const message = error instanceof Error ? error.message : String(error);
    return /dynamically imported module|Importing a module script failed|ChunkLoadError|Failed to fetch dynamically imported module|error loading dynamically imported module/i.test(
        message,
    );
}

type Props = { children: React.ReactNode };
type State = { error: Error | null };

/**
 * Root-level render error boundary. Without this, any uncaught error thrown
 * during render (most commonly a stale JS chunk 404ing after the tab was
 * left open across a deploy — see `isStaleChunkError`) unmounts the whole
 * React tree and leaves a permanently blank white page until the user
 * manually refreshes.
 *
 * Stale-chunk errors are recovered automatically with a single reload (the
 * fresh `index.html` references the new chunk hashes, so the reload
 * succeeds) rather than surfacing a manual "try again" prompt. A guard flag
 * in sessionStorage prevents a reload loop if the error persists.
 */
export default class RootErrorBoundary extends React.Component<Props, State> {
    state: State = { error: null };

    static getDerivedStateFromError(error: Error): State {
        return { error };
    }

    componentDidCatch(error: Error) {
        if (isStaleChunkError(error)) {
            let alreadyAttempted = false;
            try {
                alreadyAttempted = sessionStorage.getItem(RELOAD_GUARD_KEY) === "1";
                if (!alreadyAttempted) sessionStorage.setItem(RELOAD_GUARD_KEY, "1");
            } catch {
                // sessionStorage unavailable (private mode etc.) — fall through
                // to the manual fallback UI below instead of reload-looping.
            }
            if (!alreadyAttempted) {
                window.location.reload();
                return;
            }
        }
        // eslint-disable-next-line no-console
        console.error("Unhandled render error:", error);
    }

    handleReload = () => {
        window.location.reload();
    };

    render() {
        if (this.state.error) {
            if (isStaleChunkError(this.state.error)) {
                // Auto-reload is in flight (or was just attempted and failed) —
                // render nothing rather than flash the manual fallback.
                return null;
            }
            return (
                <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-4 text-center">
                    <p className="text-lg font-semibold text-slate-900">Something went wrong.</p>
                    <p className="max-w-sm text-sm text-slate-600">
                        This page hit an unexpected error. Reloading usually fixes it.
                    </p>
                    <button
                        type="button"
                        onClick={this.handleReload}
                        className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
                    >
                        Reload page
                    </button>
                </div>
            );
        }
        return this.props.children;
    }
}

/** Clear the reload guard once the app renders successfully, so a genuine
 * future stale-chunk error (the next deploy) can auto-recover again. */
export function clearChunkReloadGuard(): void {
    try {
        sessionStorage.removeItem(RELOAD_GUARD_KEY);
    } catch {
        // ignore
    }
}

/**
 * Vite's own signal for this failure mode — dispatched on `window` whenever
 * a dynamic `import()` it manages fails to load (network error or a 404 on a
 * chunk hash from a build that's no longer deployed). This is more reliable
 * than sniffing the thrown error's message in `isStaleChunkError`, since it
 * fires even for cases that don't cleanly propagate into a React render
 * error. Recommended by Vite's own docs for exactly this "reload once" fix.
 */
export function installViteChunkReloadListener(): void {
    window.addEventListener("vite:preloadError", () => {
        let alreadyAttempted = false;
        try {
            alreadyAttempted = sessionStorage.getItem(RELOAD_GUARD_KEY) === "1";
            if (!alreadyAttempted) sessionStorage.setItem(RELOAD_GUARD_KEY, "1");
        } catch {
            // ignore — fall through to reload attempt anyway, worst case is
            // one extra reload if this fires again.
        }
        if (!alreadyAttempted) window.location.reload();
    });
}
